import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useManagedSeo } from "@/components/pages/ManagedPage";
import { LOCALES, DEFAULT_LOCALE, localeFromPath, stripLocale, withLocale } from "@/i18n/config";

const SITE_NAME = "BEEDIGIT";
const SITE_URL = "https://www.beedigit.com";
const DEFAULT_DESCRIPTION =
  "BEEDIGIT connects machines, operators and ERP plans for real-time OEE, digital job orders, QR traceability, quality, maintenance and energy visibility.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  type?: "website" | "article";
  image?: string;
  publishedAt?: string;
  noindex?: boolean;
  /** Force canonical locale (e.g. "en" for English-only CMS content on a localized alias). */
  canonicalLocale?: string;
  /** Restrict hreflang alternates to locales that genuinely have this content. */
  alternateLocales?: string[];
  canonicalUrl?: string;
}

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  type = "website",
  image,
  publishedAt,
  noindex = false,
  canonicalLocale,
  alternateLocales,
  canonicalUrl,
}: SEOProps) => {
  const location = useLocation();
  const managed = useManagedSeo();
  if (managed) {
    title = managed.title || title;
    description = managed.description || description;
    image = managed.og_image || image;
    noindex = noindex || !!managed.noindex;
  }
  const locale = localeFromPath(location.pathname);
  const currentPath = path ?? location.pathname;
  const basePath = stripLocale(currentPath || "/");
  const cleanTitle = title?.replace(/\s*[|—–-]\s*BEEDIGIT\s*$/i, "").trim();
  const pageTitle = cleanTitle ? `${cleanTitle} | ${SITE_NAME}` : `Plug & Play MES, OEE and IIoT Platform | ${SITE_NAME}`;
  const canonical = canonicalUrl || `${SITE_URL}${withLocale(basePath, (canonicalLocale ?? locale) as typeof locale)}`;
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <html lang={locale} />
      <title>{pageTitle}</title>
      <meta name="description" content={description.length > 160 ? description.slice(0, 157).replace(/\s+\S*$/, "") + "…" : description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,follow" />}

      {/* Language alternates */}
      {!noindex && LOCALES.filter((l) => !alternateLocales || alternateLocales.includes(l.code)).map((l) => (
        <link
          key={l.code}
          rel="alternate"
          hrefLang={l.hreflang}
          href={`${SITE_URL}${withLocale(basePath, l.code)}`}
        />
      ))}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${withLocale(basePath, DEFAULT_LOCALE)}`} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImage} />
      {publishedAt && <meta property="article:published_time" content={publishedAt} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

export default SEO;

export { SITE_URL, SITE_NAME, DEFAULT_DESCRIPTION };

/** Organization JSON-LD — render once on the homepage */
export const OrganizationJsonLd = () => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        description: DEFAULT_DESCRIPTION,
        sameAs: [
          "https://www.linkedin.com/company/beedigit",
          "https://www.instagram.com/beedigit_mes",
        ],
      })}
    </script>
  </Helmet>
);

/** FAQ JSON-LD */
export const FAQJsonLd = ({ items }: { items: { question: string; answer: string }[] }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((i) => ({
          "@type": "Question",
          name: i.question,
          acceptedAnswer: { "@type": "Answer", text: i.answer },
        })),
      })}
    </script>
  </Helmet>
);

/** Article JSON-LD */
export const ArticleJsonLd = ({
  title,
  description,
  url,
  image,
  publishedAt,
  modifiedAt,
  author,
  blog = false,
}: {
  title: string;
  description?: string;
  url: string;
  image?: string;
  publishedAt?: string;
  modifiedAt?: string;
  author?: string;
  blog?: boolean;
}) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": blog ? "BlogPosting" : "Article",
        headline: title,
        ...(description && { description }),
        ...(image && { image }),
        ...(publishedAt && { datePublished: publishedAt }),
        ...(modifiedAt && { dateModified: modifiedAt }),
        ...(author && { author: { "@type": "Person", name: author } }),
        url,
        publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
      })}
    </script>
  </Helmet>
);

/** Breadcrumb JSON-LD */
export const BreadcrumbJsonLd = ({
  items,
}: {
  items: { name: string; url: string }[];
}) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      })}
    </script>
  </Helmet>
);

/** CollectionPage JSON-LD for list pages */
export const CollectionPageJsonLd = ({
  name,
  description,
  url,
}: {
  name: string;
  description: string;
  url: string;
}) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name,
        description,
        url,
        isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
      })}
    </script>
  </Helmet>
);

/** SoftwareApplication / Product JSON-LD for product pages */
export const SoftwareApplicationJsonLd = ({
  name,
  description,
  url,
  category = "BusinessApplication",
}: {
  name: string;
  description: string;
  url: string;
  category?: string;
}) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name,
        description,
        url,
        applicationCategory: category,
        operatingSystem: "Web, iOS, Android",
        publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
      })}
    </script>
  </Helmet>
);
